import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import * as Sentry from "@sentry/react";

interface QueuedOperation {
  id: string;
  operation: () => Promise<any>;
  description: string;
  timestamp: number;
}

export function useOfflineDetection() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queuedOperations, setQueuedOperations] = useState<QueuedOperation[]>([]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("You're back online! Syncing changes...");
      processQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("You're offline. Changes will sync when you're back online.", {
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {}
        }
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const queueOperation = (operation: () => Promise<any>, description: string) => {
    const queuedOp: QueuedOperation = {
      id: crypto.randomUUID(),
      operation,
      description,
      timestamp: Date.now()
    };

    setQueuedOperations(prev => [...prev, queuedOp]);
    
    if (!isOnline) {
      toast.info(`Queued: ${description}`, {
        description: "Will sync when back online"
      });
    }

    return queuedOp.id;
  };

  const processQueue = async () => {
    if (queuedOperations.length === 0) return;

    for (const op of queuedOperations) {
      try {
        await op.operation();
        toast.success(`Synced: ${op.description}`);
      } catch (error) {
        toast.error(`Failed to sync: ${op.description}`);
        Sentry.captureException(error, {
          tags: { context: 'network_error', operation: 'offline_detection', action: 'queue_sync' },
          extra: { message: 'Queue operation failed', description: op.description }
        });
      }
    }

    setQueuedOperations([]);
  };

  return {
    isOnline,
    queuedOperations,
    queueOperation,
    processQueue
  };
}